import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, Paperclip, X } from "lucide-react";
import { ChapterLabel } from "@/components/Expertise";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TOTAL = 10 * 1024 * 1024;

const inputCls = "w-full bg-transparent border border-ink/15 rounded-none px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-brass transition-colors duration-300";
const labelCls = "block text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-2.5";

const initial = { full_name: "", email: "", phone: "", service: "", languages: "", message: "" };

export default function QuoteSection() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const next = [...files, ...chosen].slice(0, 5);
    if (next.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) {
      toast.error(t("quote.form.tooBig"));
      e.target.value = "";
      return;
    }
    setFiles(next);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      fd.append("site_lang", lang);
      await axios.post(`${API}/quotes`, fd);
      toast.success(t("quote.form.success"));
      setForm(initial);
      setFiles([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("quote.form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" data-testid="quote-section" className="bg-midnight py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <ChapterLabel num="07" title={t("quote.label")} dark />
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-paper leading-[1.05]">
              {t("quote.titleA")} <em className="text-brasslight not-italic">{t("quote.titleB")}</em>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-paper/50 max-w-md">{t("quote.sub")}</p>
            <div data-testid="quote-badge" className="mt-10 border-l-2 border-brasslight pl-6 max-w-md">
              <p className="font-serif text-xl text-paper">{t("badge.title")}</p>
              <p className="text-sm text-paper/55 mt-2 leading-relaxed">{t("badge.text")}</p>
            </div>
            <div className="mt-12 space-y-6">
              <a data-testid="contact-phone" href="tel:+33607278428" className="flex items-center gap-4 text-paper/80 hover:text-brasslight transition-colors duration-300">
                <Phone size={17} strokeWidth={1.5} className="text-brasslight" />
                <span className="text-sm tracking-wide">+33 (0)6 07 27 84 28</span>
              </a>
              <a data-testid="contact-email" href="mailto:olena.gurova@gmail.com" className="flex items-center gap-4 text-paper/80 hover:text-brasslight transition-colors duration-300">
                <Mail size={17} strokeWidth={1.5} className="text-brasslight" />
                <span className="text-sm tracking-wide">olena.gurova@gmail.com</span>
              </a>
              <div data-testid="contact-address" className="flex items-start gap-4 text-paper/80">
                <MapPin size={17} strokeWidth={1.5} className="text-brasslight mt-0.5" />
                <span className="text-sm tracking-wide leading-relaxed">3 rue Lefort Gonssolin<br />76130 Mont-Saint-Aignan</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="quote-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7 bg-paper p-8 lg:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls} htmlFor="q-name">{t("quote.form.name")}</label>
                <input id="q-name" data-testid="quote-input-name" required minLength={2} className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder={t("quote.form.namePh")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-email">{t("quote.form.email")}</label>
                <input id="q-email" data-testid="quote-input-email" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder={t("quote.form.emailPh")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-phone">{t("quote.form.phone")}</label>
                <input id="q-phone" data-testid="quote-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder={t("quote.form.phonePh")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-service">{t("quote.form.service")}</label>
                <select id="q-service" data-testid="quote-select-service" required className={inputCls} value={form.service} onChange={set("service")}>
                  <option value="" disabled>{t("quote.form.choose")}</option>
                  {t("quote.form.services").map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="q-lang">{t("quote.form.languages")}</label>
                <select id="q-lang" data-testid="quote-select-languages" required className={inputCls} value={form.languages} onChange={set("languages")}>
                  <option value="" disabled>{t("quote.form.choose")}</option>
                  {t("quote.form.langs").map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="q-message">{t("quote.form.message")}</label>
                <textarea id="q-message" data-testid="quote-input-message" required minLength={10} rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder={t("quote.form.messagePh")} />
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls}>{t("quote.form.files")}</span>
                <input id="q-files" data-testid="quote-input-files" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={addFiles} />
                <label htmlFor="q-files" data-testid="quote-files-button" className="inline-flex items-center gap-3 border border-dashed border-ink/25 px-4 py-3.5 text-sm text-ink/60 hover:border-brass hover:text-ink transition-colors duration-300 cursor-pointer">
                  <Paperclip size={14} /> {t("quote.form.filesHint")}
                </label>
                {files.length > 0 && (
                  <ul data-testid="quote-files-list" className="mt-3 space-y-2">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-xs text-ink/60 border border-ink/10 px-3 py-2">
                        <span className="truncate mr-3">{f.name} — {(f.size / 1024 / 1024).toFixed(2)} Mo</span>
                        <button type="button" data-testid={`quote-file-remove-${i}`} onClick={() => removeFile(i)} className="text-ink/40 hover:text-red-600 transition-colors duration-300">
                          <X size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <button
              data-testid="quote-submit-button"
              type="submit"
              disabled={loading}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-slate2 text-paper text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-brass hover:text-midnight transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? t("quote.form.sending") : t("quote.form.submit")}
            </button>
            <p className="mt-5 text-xs text-ink/40 leading-relaxed">{t("quote.form.privacy")}</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
